import { DataTable } from '@/components/data-table/DataTable';
import { TableFilter } from '@/components/data-table/TableFilter';
import useTableFilter from '@/components/data-table/useTableFilter';
import { LpohPagination } from '@/components/pagination/LpohPagination';
import { PageSize } from '@/components/pagination/PageSize';
import { Button } from '@/components/ui/button';
import { DEFAULT_PAGE_SIZE } from '@/routes/calibration-task/list/constants';
import { Params, SortDirection } from '@/utils/types';
import { ColumnDef } from '@tanstack/react-table';
import { Await, useLoaderData, useNavigate, useSearchParams } from 'react-router';
import { FieldReport, DeploymentTypeEnum, deploymentTypeLabels, SWAP_REASONS_LABELS } from '../types';
import { TableHeaderWithSort } from '@/components/data-table/TableHeaderWithSort';
import { PAGE_SIZES } from '../../list/constants';
import { FilePlus, Pencil } from 'lucide-react';
import { getPaginatedItems } from '../../../../utils/get-paginated-items';
import { format } from 'date-fns';
import { DEFAULT_DATE_TIME_FORMAT } from '@/utils/dates';
import { MeasurementGroupLoaderResponse } from '../../types';
import { Suspense } from 'react';
import { SkeletonCard } from '@/components/skeleton-card/SkeletonCard';
import { maybeCellValue } from '@/utils/maybe-cell-value';
import { FieldReportForm } from '../FieldReportForm';
import { mergeQueryParams } from '@/utils/merge-query-params';
import { editFieldReport, createFieldReport } from '../../constants';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

const FieldReports = () => {
	const { handleFilterChange } = useTableFilter();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const { fieldReports } = useLoaderData() as MeasurementGroupLoaderResponse;

	const page = searchParams.get(Params.page) || '1';
	const limit = searchParams.get(Params.pageSize) || DEFAULT_PAGE_SIZE;
	const sortBy = (searchParams.get(Params.sortBy) || 'performedAt') as keyof FieldReport;
	const sortDirection = (searchParams.get(Params.sortDirection) || SortDirection.DESC) as SortDirection;
	const search = searchParams.get(Params.search) || '';

	const filterFieldReports = (item: FieldReport, searchValue: string) => {
		return (
			item.siteName.toLowerCase().includes(searchValue) ||
			item.msId.toString().includes(searchValue) ||
			item.staffName.toLowerCase().includes(searchValue) ||
			item.deploymentType.toLowerCase().includes(searchValue) ||
			format(item.performedAt, DEFAULT_DATE_TIME_FORMAT).includes(searchValue) ||
			item.notes.toLowerCase().includes(searchValue)
		);
	};

	const getPaginatedFieldReports = (items: FieldReport[]) => {
		return getPaginatedItems<FieldReport>({
			items,
			search,
			page,
			limit,
			sortBy,
			sortDirection,
			filterPredicate: filterFieldReports
		});
	};

	const isFormOpen = !!searchParams.get(Params.reportForm);
	const editedReportId = parseInt(searchParams.get(Params.editedReportId) ?? '', 10);

	const onCloseForm = () => {
		const queryString = mergeQueryParams(searchParams, {
			[Params.reportForm]: undefined,
			[Params.editedReportId]: undefined
		});

		navigate(`?${queryString}`);
	};

	const handleEditReport = (id: number) => {
		const queryString = mergeQueryParams(searchParams, {
			[Params.editedReportId]: id
		});
		navigate(`?${queryString}`);
	};

	const onOpenNewReportForm = () => {
		const queryString = mergeQueryParams(searchParams, {
			[Params.reportForm]: 'open'
		});

		navigate(`?${queryString}`);
	};

	const columns: ColumnDef<FieldReport>[] = [
		{
			accessorKey: 'msId',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Device ID" />
		},
		{
			accessorKey: 'siteName',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Site" />
		},
		{
			accessorKey: 'staffName',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Staff" />
		},
		{
			accessorKey: 'performedAt',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Date" />,
			cell: ({ row }) => maybeCellValue(format(row.original.performedAt, DEFAULT_DATE_TIME_FORMAT))
		},
		{
			accessorKey: 'deploymentType',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Type" />,
			cell: ({ row }) => deploymentTypeLabels[row.original.deploymentType as keyof typeof DeploymentTypeEnum]
		},
		{
			accessorKey: 'summary',
			header: 'Summary',
			cell: ({ row }) => (
				<Tooltip>
					<TooltipTrigger asChild>
						<div className="max-w-md truncate cursor-default" title={row.original.notes}>
							{row.original.notes}
						</div>
					</TooltipTrigger>
					<TooltipContent className="max-w-xs p-2">{row.original.notes}</TooltipContent>
				</Tooltip>
			)
		},
		{
			accessorKey: 'swapReason',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Swap reason" />,
			cell: ({ row }) =>
				maybeCellValue(row.original.swapReason ? SWAP_REASONS_LABELS[row.original.swapReason] : null)
		},
		{
			accessorKey: 'hasExternalAntennas',
			header: 'External antennas',
			cell: ({ row }) => maybeCellValue(row.original.hasExternalAntennas)
		},
		{
			header: 'Actions',
			cell: ({ row }) => (
				<Button
					onClick={() => handleEditReport(row.original.reportId)}
					variant="ghost"
					className="w-fit"
					title="Edit field report"
				>
					<Pencil />
				</Button>
			)
		}
	];

	return (
		<div className="flex flex-col gap-5 pt-5">
			<p className="font-bold">Field reports</p>
			<div className="w-full flex gap-5 flex-col">
				<div className="flex flex-col-reverse justify-start gap-2 md:flex-row md:justify-between">
					<TableFilter handleFilterChange={handleFilterChange} />
				</div>

				<Button
					type="button"
					variant="outline"
					onClick={onOpenNewReportForm}
					className="flex justify-start w-fit"
				>
					<FilePlus />
					New report
				</Button>

				<FieldReportForm
					isOpen={isFormOpen}
					onClose={onCloseForm}
					onSubmit={(id, values) => createFieldReport(id, values)}
					confirmationMessage={'Are you sure you want to create this field report?'}
					submitSuccessMessage={'Field report created successfuly.'}
				/>

				<Suspense fallback={<SkeletonCard />}>
					<Await resolve={fieldReports}>
						{fieldReports => {
							const { paginatedItems, totalPages } = getPaginatedFieldReports(fieldReports.data);

							const editedFieldReport = fieldReports.data.find(r => r.reportId === editedReportId);

							return (
								<>
									{editedFieldReport && (
										<FieldReportForm
											isOpen
											fieldReport={editedFieldReport}
											onClose={onCloseForm}
											onSubmit={(id, values) => editFieldReport(id, editedReportId, values)}
											confirmationMessage={'Are you sure you want to edit this field report?'}
											submitSuccessMessage={'Changes saved successfuly.'}
										/>
									)}

									<DataTable columns={columns} data={paginatedItems} isLoading={false} />
									<div className="flex justify-between">
										<PageSize pageSize={limit} options={PAGE_SIZES} />
										<LpohPagination currentPage={parseInt(page)} totalPages={totalPages} />
									</div>
								</>
							);
						}}
					</Await>
				</Suspense>
			</div>
		</div>
	);
};

export default FieldReports;
